'use client';
import React, { useEffect, useState, useCallback } from 'react';
import FilmRail from '@/components/ui/FilmRail';
import { filmService } from '@/lib/services/filmService';
import type { Film } from '@/lib/services/filmService';
import type { ContinueWatchingItem } from '@/lib/services/filmService';
import type { FilmCardData } from '@/components/ui/FilmCard';
import { watchlistService, type WatchlistItem } from '@/lib/services/watchlistService';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

function toCardData(f: Film): FilmCardData {
  return {
    id: f.id,
    title: f.title,
    year: f.year,
    runtime: f.runtimeDisplay || `${f.runtimeMinutes}m`,
    type: f.filmType as any,
    poster: f.posterUrl,
    posterAlt: f.posterAlt || `${f.title} poster`,
    genres: f.genres,
    festivalBadge: f.festivalBadge || undefined,
    rating: f.contentRating,
    isNew: f.isNew,
    isStaffPick: f.isStaffPick,
  };
}

function watchlistItemToCard(item: WatchlistItem): FilmCardData | null {
  const f = item.films;
  if (!f) return null;
  return {
    id: item.film_id,
    title: f.title,
    year: f.year,
    runtime: f.runtime_display || '',
    type: (f.film_type as any) || 'short',
    poster: f.poster_url,
    posterAlt: f.poster_alt || f.title,
    genres: f.genres || [],
    festivalBadge: f.festival_badge || undefined,
    rating: f.content_rating,
    isNew: f.is_new,
    isStaffPick: f.is_staff_pick,
  };
}

export default function HomepageRails() {
  const [films, setFilms] = useState<Film[]>([]);
  const [continueWatching, setContinueWatching] = useState<ContinueWatchingItem[]>([]);
  const [personalized, setPersonalized] = useState<{
    recommendedForYou: Film[];
    becauseYouWatched: Film[];
    topGenre: { label: string; films: Film[] } | null;
    highlyCompleted: Film[];
    watchedGenres: string[];
    isPersonalized: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // Watchlist state
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
  const [watchlistIds, setWatchlistIds] = useState<Set<string>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check auth and load watchlist
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsAuthenticated(!!user);
      if (user) {
        watchlistService.getWatchlist().then(items => {
          setWatchlistItems(items);
          setWatchlistIds(new Set(items.map(i => i.film_id)));
        });
      }
    });

    Promise.all([
      filmService.getPublished(),
      filmService.getContinueWatching(),
      filmService.getPersonalizedRails(),
    ]).then(([published, continueList, personalizedData]) => {
      setFilms(published);
      setContinueWatching(continueList);
      setPersonalized(personalizedData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleToggleWatchlist = useCallback(async (filmId: string) => {
    const inList = watchlistIds.has(filmId);
    // Optimistic update
    setWatchlistIds(prev => {
      const next = new Set(prev);
      if (inList) next.delete(filmId);
      else next.add(filmId);
      return next;
    });
    if (inList) {
      setWatchlistItems(prev => prev.filter(i => i.film_id !== filmId));
    }
    const newState = await watchlistService.toggle(filmId, inList);
    if (newState !== !inList) {
      // Revert on failure
      setWatchlistIds(prev => {
        const next = new Set(prev);
        if (inList) next.add(filmId);
        else next.delete(filmId);
        return next;
      });
    } else if (!inList) {
      // Reload watchlist to get film details for the new item
      watchlistService.getWatchlist().then(items => {
        setWatchlistItems(items);
        setWatchlistIds(new Set(items.map(i => i.film_id)));
      });
    }
  }, [watchlistIds]);

  if (loading) {
    return (
      <div className="flex flex-col gap-8 sm:gap-10 px-4 sm:px-6 lg:px-10">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`rail-skel-${i}`}>
            <div className="h-5 w-48 rounded skeleton-pulse mb-4" />
            <div className="flex gap-3 overflow-hidden">
              {Array.from({ length: 6 }).map((_, j) => (
                <div key={`card-skel-${j}`} className="w-32 sm:w-40 shrink-0">
                  <div className="aspect-[2/3] rounded-lg skeleton-pulse" />
                  <div className="h-3 rounded skeleton-pulse mt-2 w-3/4" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const continueWatchingCards: FilmCardData[] = continueWatching.map(({ film, resumeSeconds, progressPercent }) => ({
    ...toCardData(film),
    progressPercent,
    resumeSeconds,
  }));
  const newThisWeek = films.filter(f => f.isNew).map(toCardData);
  const staffPicks = films.filter(f => f.isStaffPick).map(toCardData);
  const shortFilms = films.filter(f => f.filmType === 'short').map(toCardData);
  const documentaries = films.filter(f => f.filmType === 'documentary').map(toCardData);
  const features = films.filter(f => f.filmType === 'feature').map(toCardData);
  const animations = films.filter(f => f.filmType === 'animation').map(toCardData);
  const festivalFilms = films.filter(f => f.festivalBadge).map(toCardData);

  // Personalized rails (when user has watch history)
  const isPersonalized = personalized?.isPersonalized ?? false;
  const recommendedCards = isPersonalized
    ? personalized!.recommendedForYou.map(toCardData)
    : [...films].reverse().slice(0, 8).map(toCardData);

  const becauseYouWatchedCards = isPersonalized
    ? personalized!.becauseYouWatched.map(toCardData)
    : [];

  const topGenreRail = isPersonalized && personalized!.topGenre
    ? { label: personalized!.topGenre.label, cards: personalized!.topGenre.films.map(toCardData) }
    : null;

  const highlyCompletedCards = isPersonalized
    ? personalized!.highlyCompleted.map(toCardData)
    : [];

  // Watchlist cards
  const watchlistCards = watchlistItems
    .map(watchlistItemToCard)
    .filter((c): c is FilmCardData => c !== null);

  const railProps = {
    watchlistIds,
    onToggleWatchlist: handleToggleWatchlist,
  };

  return (
    <div className="flex flex-col gap-8 sm:gap-10">
      {/* My Watchlist quick-access rail — only shown when authenticated and has items */}
      {isAuthenticated && watchlistCards.length > 0 && (
        <div className="relative px-4 sm:px-6 lg:px-10">
          <div className="flex items-end justify-between mb-3 sm:mb-4">
            <div>
              <h2 className="text-sm sm:text-base font-bold text-foreground leading-tight flex items-center gap-2">
                <Icon name="BookmarkIcon" size={16} className="text-primary" />
                My Watchlist
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">Films you've saved for later</p>
            </div>
            <Link
              href="/user-profile"
              className="text-xs text-primary hover:text-accent transition-colors flex items-center gap-1"
            >
              View all
              <Icon name="ChevronRightIcon" size={12} />
            </Link>
          </div>
          <FilmRail
            title=""
            films={watchlistCards.slice(0, 12)}
            cardSize="md"
            {...railProps}
          />
        </div>
      )}

      {continueWatchingCards.length > 0 && (
        <FilmRail
          title="Continue Watching"
          subtitle="Pick up where you left off"
          films={continueWatchingCards}
          cardSize="md"
          {...railProps}
        />
      )}

      {recommendedCards.length > 0 && (
        <FilmRail
          title="Recommended For You"
          subtitle={isPersonalized ? 'Based on your watch history & genre interests' : 'Based on your taste profile'}
          films={recommendedCards}
          cardSize="md"
          {...railProps}
        />
      )}

      {becauseYouWatchedCards.length > 0 && (
        <FilmRail
          title="Because You Watched"
          subtitle="More films like what you've enjoyed"
          films={becauseYouWatchedCards}
          cardSize="md"
          {...railProps}
        />
      )}

      {newThisWeek.length > 0 && (
        <FilmRail
          title="New This Week"
          subtitle="Just added"
          films={newThisWeek}
          cardSize="md"
          {...railProps}
        />
      )}

      {topGenreRail && topGenreRail.cards.length > 0 && (
        <FilmRail
          title={`More ${topGenreRail.label}`}
          subtitle={`Your most-watched genre`}
          films={topGenreRail.cards}
          cardSize="md"
          {...railProps}
        />
      )}

      {shortFilms.length > 0 && (
        <FilmRail
          title="Award-Winning Shorts"
          subtitle="Festival favorites under 40 minutes"
          films={shortFilms}
          cardSize="sm"
          {...railProps}
        />
      )}

      {staffPicks.length > 0 && (
        <FilmRail
          title="Staff Picks"
          subtitle="Handpicked by our curatorial team"
          films={staffPicks}
          cardSize="md"
          {...railProps}
        />
      )}

      {highlyCompletedCards.length > 0 && (
        <FilmRail
          title="Highly Watched"
          subtitle="Films audiences watch all the way through"
          films={highlyCompletedCards}
          cardSize="md"
          {...railProps}
        />
      )}

      {documentaries.length > 0 && (
        <FilmRail
          title="Documentaries"
          subtitle="Real stories, extraordinary lives"
          films={documentaries}
          cardSize="lg"
          {...railProps}
        />
      )}

      {animations.length > 0 && (
        <FilmRail
          title="Animation"
          subtitle="From student films to festival darlings"
          films={animations}
          cardSize="sm"
          {...railProps}
        />
      )}

      {festivalFilms.length > 0 && (
        <FilmRail
          title="Festival Favorites"
          subtitle="Cannes, Sundance, Berlin, TIFF, and beyond"
          films={festivalFilms}
          cardSize="md"
          {...railProps}
        />
      )}

      {features.length > 0 && (
        <FilmRail
          title="Feature Films"
          subtitle="Full-length independent cinema"
          films={features}
          cardSize="lg"
          {...railProps}
        />
      )}
    </div>
  );
}